import React, { useState } from 'react';
import { Box, Typography, IconButton, useTheme, Modal, Menu, MenuItem } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import eyeIcon from 'packages/novous_web/src/assets/icons/Eye.svg';
import menuIcon from 'packages/novous_web/src/assets/icons/new-menu.svg';
import OpenInFullIcon from 'packages/novous_web/src/assets/icons/Expand.svg';
import EditIcon from 'packages/novous_web/src/assets/icons/edit.svg';
import RestartAltIcon from 'packages/novous_web/src/assets/icons/Reset.svg';
import CollapseIcon from 'packages/novous_web/src/assets/icons/CollapseIcon.svg';
import CommonAnalysisTable from './CommonAnalysisTable';
import EditDataModal from './EditDataModal';
import { useToast } from 'packages/novous_web/src/context/ToastContext';
import NoDataIcon from 'packages/novous_web/src/assets/icons/NoData.svg';

interface ScatterDataPoint {
  x: number;
  y: number;
  vehicle: string;
  size: number;
  vehicle_id?: string;
  total_km_driven?: number;
  originalData?: any;
}

interface CommonScatterChartProps {
  title: string;
  infoIcon?: React.ReactNode;
  data?: ScatterDataPoint[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  containerClassName?: string;
  tableColumns?: Array<{ key: string; label: string; align?: 'left' | 'center' | 'right' }>;
  tableData?: any[];
  tableTitle?: string;
  tableHeaderText?: string;
  paginationInfo?: any;
  onTableIconClick?: () => void;
  onPaginationChange?: (cursor: any) => void;
  chartId?: number;
  onCardDataUpdate?: (chartId: number, startDate: string, endDate: string) => void;
}

const CommonScatterChart: React.FC<CommonScatterChartProps> = ({
  title,
  infoIcon,
  data = [],
  xAxisLabel = 'Low Speed Hours',
  yAxisLabel = 'Eco Driving Hours',
  containerClassName = '',
  tableColumns = [],
  tableData = [],
  tableTitle = 'Analysis Table',
  tableHeaderText = 'Detailed information',
  paginationInfo,
  onTableIconClick,
  onPaginationChange,
  chartId,
  onCardDataUpdate,
}) => {
  const theme = useTheme();
  const { showToast } = useToast();
  const [isTableOpen, setIsTableOpen] = useState<boolean>(false);
  const [isExpandedModalOpen, setIsExpandedModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [resetTrigger, setResetTrigger] = useState<number>(0);
  const open = Boolean(anchorEl);
  const [page, setPage] = useState(0);
  const ITEMS_PER_PAGE = 5;

  // Use provided data or empty array
  const chartData = data;

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpand = () => {
    console.log('Expand clicked for:', title);
    setIsExpandedModalOpen(true);
    handleMenuClose();
  };

  const handleEditData = () => {
    console.log('Edit Data clicked for:', title);
    setIsEditModalOpen(true);
    handleMenuClose();
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleApplyEditData = (data: any) => {
    console.log('Applied edit data:', data);
    if (onCardDataUpdate && chartId) {
      onCardDataUpdate(chartId, data.startDate, data.endDate);
      const startDateFormatted = new Date(data.startDate).toLocaleDateString('en-GB');
      const endDateFormatted = new Date(data.endDate).toLocaleDateString('en-GB');
      showToast(`Date range applied: ${startDateFormatted} to ${endDateFormatted}`, 'success');
    }
  };

  const handleResetChanges = () => {
    console.log('Reset Changes clicked for:', title);
    handleMenuClose();
    setIsEditModalOpen(false);
    setResetTrigger((prev) => prev + 1);
    if (onCardDataUpdate && chartId) {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      onCardDataUpdate(chartId, startDate, endDate);
    }
  };

  const handleCloseExpandedModal = () => {
    setIsExpandedModalOpen(false);
  };
  const calculateDomains = (dataToUse: any[]) => {
    if (dataToUse.length === 0) {
      return { xDomain: [0, 8], yDomain: [0, 1] };
    }

    const xValues = dataToUse.map((d) => d.x);
    const yValues = dataToUse.map((d) => d.y);

    const xMin = Math.max(0, Math.floor(Math.min(...xValues) * 0.9));
    const xMax = Math.ceil(Math.max(...xValues) * 1.1);
    const yMin = Math.max(0, Math.floor(Math.min(...yValues) * 0.9));
    const yMax = Math.ceil(Math.max(...yValues) * 1.1);

    return {
      xDomain: [xMin, xMax],
      yDomain: [yMin, yMax],
    };
  };

  const handleTable = () => {
    setIsTableOpen(!isTableOpen);
    if (onTableIconClick) {
      onTableIconClick();
    }
  };

  // Define a color palette for vehicles
  const colorPalette = [
    '#B2ADEA', // Purple
    '#A9DFBF', // Green
    '#E9ADAD', // Pink
    '#FAD6B7', // Orange
    '#ACCCF8', // Blue
    '#D7B9F3', // Indigo
    '#f1d88cc5', // Yellow
    '#60dd94ff', // Dark Green
    '#df5e5eff', // Red
    '#5bbaf1ff', // Dark Blue
  ];

  // Group data by vehicle_id and create dynamic colors
  const groupedDataTemp = chartData.reduce((acc: any, point) => {
    const vehicleId = point.vehicle_id || point.vehicle || 'Unknown';
    if (!acc[vehicleId]) {
      acc[vehicleId] = [];
    }
    acc[vehicleId].push(point);
    return acc;
  }, {});

  // Get all vehicles (not limited to 6)
  const allVehicleIds = Object.keys(groupedDataTemp).sort();

  // Pagination logic
  const totalPages = Math.ceil(allVehicleIds.length / ITEMS_PER_PAGE);
  const handlePrev = () => {
    setPage((prev) => Math.max(prev - 1, 0));
  };
  const handleNext = () => {
    setPage((prev) => Math.min(prev + 1, totalPages - 1));
  };

  // Get paginated vehicle IDs
  const paginatedVehicleIds = allVehicleIds.slice(
    page * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE + ITEMS_PER_PAGE,
  );

  const groupedData: { [key: string]: any } = {};
  paginatedVehicleIds.forEach((vehicleId) => {
    groupedData[vehicleId] = groupedDataTemp[vehicleId];
  });

  // Filter chartData to only include paginated vehicles
  const filteredChartData = chartData.filter((point) =>
    paginatedVehicleIds.includes(point.vehicle_id || point.vehicle || 'Unknown'),
  );

  // Create vehicle colors mapping based on actual vehicle IDs
  const vehicleColors: { [key: string]: string } = {};
  allVehicleIds.forEach((vehicleId, index) => {
    vehicleColors[vehicleId] = colorPalette[index % colorPalette.length];
  });

  // Calculate domains based on filtered data
  const domains = calculateDomains(filteredChartData);

  const CustomDot = (props: any) => {
    const { cx, cy, payload, fill } = props;

    // Get total_km_driven from the data point
    const totalKmDriven = payload.total_km_driven || 0;

    // Calculate radius based on total_km_driven using your rules
    let radius;

    if (totalKmDriven <= 100) {
      radius = 2; // 10px diameter = 5px radius
    } else if (totalKmDriven > 100 && totalKmDriven <= 200) {
      radius = 5; // 20px diameter = 10px radius
    } else if (totalKmDriven > 200 && totalKmDriven <= 300) {
      radius = 10; // 30px diameter = 15px radius
    } else if (totalKmDriven > 300 && totalKmDriven <= 400) {
      radius = 12.5; // 35px diameter = 17.5px radius
    } else if (totalKmDriven > 400) {
      radius = 15; // 40px diameter = 20px radius
    } else {
      radius = 2; // Default
    }

    return <circle cx={cx} cy={cy} r={radius} fill={fill} fillOpacity={0.7} stroke={fill} strokeWidth={1} />;
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg min-w-[180px]">
          <div className="text-left mb-2">
            <p className="font-semibold text-gray-800 text-sm">{data.vehicle_id || data.vehicle}</p>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 text-xs">{xAxisLabel}</span>
              <span className="font-semibold text-gray-800 text-sm">{data.x.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 text-xs">{yAxisLabel}</span>
              <span className="font-semibold text-gray-800 text-sm">{data.y.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 text-xs">Total KM</span>
              <span className="font-semibold text-gray-800 text-sm">
                {data.total_km_driven?.toFixed(2) || 'N/A'}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {/* Main Chart Container */}
      {chartData.length === 0 ? (
        <Box
          className={`w-full h-full flex flex-col border rounded-md p-4 items-start mt-2 bg-white ${containerClassName}`}
          sx={{ gap: 1 }}
        >
          <Box className="w-full flex justify-between items-center">
            <Box className="flex items-center" sx={{ gap: 1 }}>
              <Typography
                variant="h6"
                fontSize={18}
                sx={(theme) => ({ color: theme.palette.text.primary, fontWeight: 600 })}
              >
                {title}
              </Typography>
              {infoIcon && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: 16,
                    height: 16,
                    flexShrink: 0,
                  }}
                >
                  {infoIcon}
                </Box>
              )}
            </Box>
            <Box className="flex items-center" sx={{ gap: 1 }}>
              <Box
                className="flex items-center cursor-pointer"
                onClick={handleTable}
                sx={{ pt: '10px', gap: 0.3 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    flexShrink: 0,
                  }}
                >
                  <img src={eyeIcon} alt="Table" width={24} height={24} style={{ display: 'block' }} />
                </Box>
                <Typography
                  variant="body2"
                  fontSize={14}
                  sx={(theme) => ({ color: theme.palette.primary.main })}
                >
                  Table
                </Typography>
              </Box>
              <Box
                className="flex items-center cursor-pointer"
                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                  handleMenuClick(e as any);
                }}
                sx={{ pt: '10px', gap: 0.3 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={menuIcon}
                    alt="Menu"
                    width="24"
                    height="24"
                    style={{
                      display: 'block',
                      width: '24px',
                      height: '24px',
                      minWidth: '24px',
                      minHeight: '24px',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              width: '100%',
              flex: 1,
              minHeight: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
          </Box>
        </Box>
      ) : (
        <Box
          className={`w-full h-full flex flex-col border rounded-md p-4 items-start mt-2 bg-white ${containerClassName}`}
          sx={{ gap: 1 }}
        >
          <Box className="w-full flex justify-between items-center">
            <Box className="flex items-center" sx={{ gap: 1 }}>
              <Typography
                variant="h6"
                fontSize={18}
                sx={(theme) => ({ color: theme.palette.text.primary, fontWeight: 600 })}
              >
                {title}
              </Typography>
              {infoIcon && (
                <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16, flexShrink: 0 }}>
                  {infoIcon}
                </Box>
              )}
            </Box>
            <Box className="flex items-center" sx={{ gap: 1 }}>
              <Box
                className="flex items-center cursor-pointer"
                onClick={handleTable}
                sx={{ pt: '10px', gap: 0.3 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    flexShrink: 0,
                  }}
                >
                  <img src={eyeIcon} alt="Table" width={24} height={24} style={{ display: 'block' }} />
                </Box>
                <Typography
                  variant="body2"
                  fontSize={14}
                  sx={(theme) => ({ color: theme.palette.primary.main })}
                >
                  Table
                </Typography>
              </Box>
              <Box
                className="flex items-center cursor-pointer"
                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                  handleMenuClick(e as any);
                }}
                sx={{ pt: '10px', gap: 0.3 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    flexShrink: 0,
                  }}
                >
                  <img src={menuIcon} alt="Menu" width="24" height="24" style={{ display: 'block' }} />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Custom Legend with Pagination */}
          {chartData && chartData.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                gap: 1,
              }}
            >
              {page > 0 && (
                <IconButton
                  size="small"
                  onClick={handlePrev}
                  sx={{
                    p: 0.5,
                    color: theme.palette.text.primary,
                    flexShrink: 0,
                  }}
                >
                  <ArrowBackIosNewIcon sx={{ width: 16, height: 16, opacity: 1 }} />
                </IconButton>
              )}

              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: 1, sm: 2, md: 3 },
                  flexGrow: 1,
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  overflow: 'hidden',
                  flexWrap: 'nowrap',
                  minWidth: 0,
                }}
              >
                {paginatedVehicleIds.map((vehicleId) => (
                  <Box
                    key={vehicleId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '14px',
                      flexShrink: 1,
                      minWidth: 0,
                      maxWidth: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: vehicleColors[vehicleId],
                        borderRadius: '50%',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: { xs: '12px', sm: '14px' },
                        lineHeight: '21px',
                        color: '#1B1B1B',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {vehicleId}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {page < totalPages - 1 && (
                <IconButton
                  size="small"
                  onClick={handleNext}
                  sx={{
                    p: 0.5,
                    color: theme.palette.text.primary,
                    flexShrink: 0,
                  }}
                >
                  <ArrowForwardIosIcon sx={{ width: 16, height: 16, opacity: 1 }} />
                </IconButton>
              )}
            </Box>
          )}

          {/* Chart */}
          <Box className="w-full">
            <div className="flex">
              <Typography
                variant="caption"
                color="textPrimary"
                sx={{
                  writingMode: 'vertical-rl',
                  transform: 'rotate(180deg)',
                  textAlign: 'center',
                }}
              >
                {yAxisLabel}
              </Typography>
              <div className="flex-1">
                <ResponsiveContainer width="100%" height={450}>
                  <ScatterChart
                    margin={{
                      top: 20,
                      right: 20,
                      bottom: 20,
                      left: 20,
                    }}
                  >
                    <CartesianGrid stroke="#E6E9EC" />
                    <XAxis
                      type="number"
                      dataKey="x"
                      name={xAxisLabel}
                      domain={domains.xDomain}
                      tickCount={Math.min(9, domains.xDomain[1] - domains.xDomain[0] + 1)}
                      tickFormatter={(value) => {
                        const [min, max] = domains.xDomain;
                        if (value === min || value === max) return ''; // hide first & last tick
                        return Math.round(value).toString();
                      }}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12 }}
                      dy={10}
                    />
                    <YAxis
                      type="number"
                      dataKey="y"
                      name={yAxisLabel}
                      domain={domains.yDomain}
                      tickCount={Math.min(6, domains.yDomain[1] - domains.yDomain[0] + 1)}
                      tickFormatter={(value) => Math.round(value).toString()}
                      width={15}
                      axisLine={false} // <-- remove y-axis line
                      tickLine={false}
                      tick={{ fontSize: 14 }}
                    />
                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={false}
                      contentStyle={{
                        background: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                      }}
                    />

                    {/* Render scatter plots for each vehicle */}
                    {Object.entries(groupedData).map(([vehicle, points]: [string, any]) => (
                      <Scatter
                        key={vehicle}
                        name={vehicle}
                        data={points}
                        fill={
                          vehicleColors[vehicle as keyof typeof vehicleColors] || theme.palette.primary.main
                        }
                        shape={<CustomDot />}
                      />
                    ))}
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="w-full flex items-center justify-center gap-4">
              <Typography variant="caption" color="textPrimary" align="center">
                {xAxisLabel}
              </Typography>
            </div>
          </Box>
        </Box>
      )}

      {/* Table Modal */}
      <Modal
        open={isTableOpen}
        onClose={() => setIsTableOpen(false)}
        aria-labelledby="scatter-table-modal"
        aria-describedby="scatter-table-description"
      >
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            height: '90%',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <CommonAnalysisTable
            title={tableTitle}
            headerText={tableHeaderText}
            columns={tableColumns}
            data={tableData}
            noDataMessage="No data available for this selection"
            onClose={() => setIsTableOpen(false)}
            paginationInfo={paginationInfo}
            onPaginationChange={onPaginationChange}
          />
        </Box>
      </Modal>

      {/* Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '160px',
          },
        }}
      >
        <MenuItem onClick={handleExpand} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={OpenInFullIcon} alt="Expand" style={{ width: 24, height: 24 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Expand</Typography>
        </MenuItem>
        <MenuItem onClick={handleEditData} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Edit Data</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleResetChanges}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}
        >
          <img
            src={RestartAltIcon}
            alt="Reset"
            style={{
              width: 20,
              height: 20,
              filter:
                'invert(34%) sepia(68%) saturate(2449%) hue-rotate(338deg) brightness(91%) contrast(84%)',
            }}
          />
          <Typography sx={{ fontSize: '14px', color: '#DC3545' }}>Reset Changes</Typography>
        </MenuItem>
      </Menu>

      {/* Expanded Modal */}
      <Modal open={isExpandedModalOpen} onClose={handleCloseExpandedModal}>
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '70%',
            height: '85%',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
                {title}
              </Typography>
              {infoIcon && (
                <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16 }}>{infoIcon}</Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                onClick={handleEditData}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={EditIcon} alt="Edit" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: theme.palette.primary.main,
                  }}
                >
                  Edit Chart
                </Typography>
              </Box>
              <Box
                onClick={handleCloseExpandedModal}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={CollapseIcon} alt="Collapse" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: theme.palette.primary.main,
                  }}
                >
                  Collapse
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Custom Legend with Pagination */}
          {chartData && chartData.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                gap: 1,
              }}
            >
              {page > 0 && (
                <IconButton
                  size="small"
                  onClick={handlePrev}
                  sx={{
                    p: 0.5,
                    color: theme.palette.text.primary,
                    flexShrink: 0,
                  }}
                >
                  <ArrowBackIosNewIcon sx={{ width: 16, height: 16, opacity: 1 }} />
                </IconButton>
              )}

              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: 1, sm: 2, md: 3 },
                  flexGrow: 1,
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  overflow: 'hidden',
                  flexWrap: 'nowrap',
                  minWidth: 0,
                }}
              >
                {paginatedVehicleIds.map((vehicleId) => (
                  <Box
                    key={vehicleId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '14px',
                      flexShrink: 1,
                      minWidth: 0,
                      maxWidth: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: vehicleColors[vehicleId],
                        borderRadius: '50%',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: { xs: '12px', sm: '14px' },
                        lineHeight: '21px',
                        color: '#1B1B1B',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {vehicleId}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {page < totalPages - 1 && (
                <IconButton
                  size="small"
                  onClick={handleNext}
                  sx={{
                    p: 0.5,
                    color: theme.palette.text.primary,
                    flexShrink: 0,
                  }}
                >
                  <ArrowForwardIosIcon sx={{ width: 16, height: 16, opacity: 1 }} />
                </IconButton>
              )}
            </Box>
          )}

          {/* Chart Content - Flex grow to fill space */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, mb: 2 }}>
            {chartData && chartData.length > 0 ? (
              <Box className="w-full h-full flex flex-col">
                <div className="flex h-full">
                  <Typography
                    variant="caption"
                    color="textPrimary"
                    sx={{
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      textAlign: 'center',
                    }}
                  >
                    {yAxisLabel}
                  </Typography>
                  <div className="flex-1">
                    <ResponsiveContainer width="100%" height="100%">
                      <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" vertical={false} />
                        <XAxis
                          type="number"
                          dataKey="x"
                          name={xAxisLabel}
                          domain={calculateDomains(filteredChartData).xDomain}
                          tick={{ fontSize: 12 }}
                          axisLine={{ stroke: '#E5E5E5' }}
                          tickLine={false}
                          padding={{ left: 30, right: 30 }}
                          dy={10}
                        />
                        <YAxis
                          type="number"
                          dataKey="y"
                          name={yAxisLabel}
                          domain={calculateDomains(filteredChartData).yDomain}
                          tick={{ fontSize: 14 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={false} />

                        {/* Render scatter plots for each vehicle */}
                        {Object.entries(groupedData).map(([vehicle, points]: [string, any]) => (
                          <Scatter
                            key={vehicle}
                            name={vehicle}
                            data={points}
                            fill={
                              vehicleColors[vehicle as keyof typeof vehicleColors] ||
                              theme.palette.primary.main
                            }
                            shape={<CustomDot />}
                          />
                        ))}
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="w-full flex items-center justify-center gap-4">
                  <Typography variant="caption" color="textPrimary" align="center">
                    {xAxisLabel}
                  </Typography>
                </div>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  flex: 1,
                }}
              >
                <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Edit Data Modal */}
      <EditDataModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        onApply={handleApplyEditData}
        cardTitle={title}
        chartId={chartId}
        resetTrigger={resetTrigger}
      />
    </>
  );
};

export default CommonScatterChart;
